import { useMemo } from "react";
import { AxiosError } from "axios";
import { useMutation } from "react-query";

import activities from "../../../redux/activities";
import { Operation, CreatorRole } from "../../../models";
import { MutationEnvelope, ensureMutationState } from "../../../services/query";
import operationsService from "../../../services/operations";
import { tripInsightsDataDirtyFlag } from "../../../services/insights";

import {
  useMaybeCurrentOperationId,
  useClearCurrentOperationId,
} from "./currentOperationId";
import { useInvalidateCurrentOperation } from "./currentOperation";
import { useSetCurrentOperationDataGlobalActivityAndStatus } from "./currentOperation/currentOperation.globalStatus";
import { useInvalidatePreOperations } from "./preOperations";

import { useInvalidateOperations } from "./operations.invalidate";
import { useSetOperationsGlobalActivityAndStatus } from "./operations.globalStatus";
import { useMaybeFabricFeatureFlag } from "../featureFlags";

// ########################
// ## useUpdateOperation ##
// ########################

export type ResultOfUseUpdateOperation = MutationEnvelope<Operation>;
export type UseUpdateOperation = () => ResultOfUseUpdateOperation;

/**
 * Use the `mutate` function returned from this hook to update the operation.
 * This will trigger an invalidation of the operations data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useUpdateOperation: UseUpdateOperation = () => {
  const currentOperationId = useMaybeCurrentOperationId();
  const invalidateOperations = useInvalidateOperations();
  const invalidateCurrentOperation = useInvalidateCurrentOperation();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const setOperationsGlobalActivityAndStatus =
    useSetOperationsGlobalActivityAndStatus();

  const setCurrentOperationDataGlobalActivityAndStatus =
    useSetCurrentOperationDataGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    Operation,
    AxiosError,
    Operation
  >((operation) => operationsService.updateOperation(operation, catalogFlag), {
    onMutate: (operation) => {
      setOperationsGlobalActivityAndStatus({
        status: "loading",
        activity: activities.updateOperation,
      });

      if (currentOperationId === operation.id) {
        setCurrentOperationDataGlobalActivityAndStatus({
          activity: activities.updateCurrentOperation,
          status: "loading",
        });
      }
    },
    onSuccess: async (_, operation) => {
      if (currentOperationId === operation.id) {
        await invalidateCurrentOperation();
      } else {
        await invalidateOperations();
      }

      setOperationsGlobalActivityAndStatus({
        status: "success",
        activity: activities.updateOperation,
      });

      if (currentOperationId === operation.id) {
        setCurrentOperationDataGlobalActivityAndStatus({
          activity: activities.updateCurrentOperation,
          status: "success",
        });
      }
    },
    onError: (_, operation) => {
      setOperationsGlobalActivityAndStatus({
        status: "error",
        activity: activities.updateOperation,
      });

      if (currentOperationId === operation.id) {
        setCurrentOperationDataGlobalActivityAndStatus({
          activity: activities.updateCurrentOperation,
          status: "error",
        });
      }
    },
  });

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// ########################
// ## useDeleteOperation ##
// ########################

export type ResultOfUseDeleteOperation = MutationEnvelope<string, null>;
export type UseDeleteOperation = () => ResultOfUseDeleteOperation;

/**
 * Use the `mutate` function returned from this hook to delete the operation.
 * This will trigger an invalidation of the operations data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useDeleteOperation: UseDeleteOperation = () => {
  const currentOperationId = useMaybeCurrentOperationId();
  const invalidatePreOperations = useInvalidatePreOperations();
  const invalidateOperations = useInvalidateOperations();
  const clearCurrentOperationId = useClearCurrentOperationId();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const setOperationsGlobalActivityAndStatus =
    useSetOperationsGlobalActivityAndStatus();

  const setCurrentOperationDataGlobalActivityAndStatus =
    useSetCurrentOperationDataGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    null,
    AxiosError,
    string
  >(
    (operationId) =>
      operationsService.removeOperation(operationId, catalogFlag),
    {
      onMutate: (operationId) => {
        setOperationsGlobalActivityAndStatus({
          activity: activities.deleteOperation,
          status: "loading",
        });

        if (currentOperationId === operationId) {
          setCurrentOperationDataGlobalActivityAndStatus({
            activity: activities.deleteCurrentOperation,
            status: "loading",
          });
        }
      },
      onSuccess: async (_, operationId) => {
        if (currentOperationId === operationId) {
          await invalidatePreOperations();
          await clearCurrentOperationId();
        } else {
          await invalidateOperations();
        }

        setOperationsGlobalActivityAndStatus({
          activity: activities.deleteOperation,
          status: "success",
        });

        if (currentOperationId === operationId) {
          setCurrentOperationDataGlobalActivityAndStatus({
            activity: activities.deleteCurrentOperation,
            status: "success",
          });
        }

        tripInsightsDataDirtyFlag();
      },
      onError: (_, operationId) => {
        setOperationsGlobalActivityAndStatus({
          activity: activities.deleteOperation,
          status: "error",
        });

        if (currentOperationId === operationId) {
          setCurrentOperationDataGlobalActivityAndStatus({
            activity: activities.deleteCurrentOperation,
            status: "error",
          });
        }
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// ########################
// ## useCreateOperation ##
// ########################

export type NewOperation = {
  operationName: string;
  creatorRole: CreatorRole;
};
export type ResultOfUseCreateOperation = MutationEnvelope<
  NewOperation,
  Operation
>;
export type UseCreateOperation = () => ResultOfUseCreateOperation;

/**
 * Use the `mutate` function returned from this hook to create a new operation.
 * This will trigger an invalidation of the operations data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useCreateOperation: UseCreateOperation = () => {
  const invalidateOperations = useInvalidateOperations();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });

  const setOperationsGlobalActivityAndStatus =
    useSetOperationsGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    Operation,
    AxiosError,
    NewOperation
  >(
    (operation) =>
      operationsService.addOperation(
        operation.operationName,
        operation.creatorRole,
        catalogFlag,
      ),
    {
      onMutate: () => {
        setOperationsGlobalActivityAndStatus({
          activity: activities.addOperation,
          status: "loading",
        });
      },
      onSuccess: async () => {
        tripInsightsDataDirtyFlag();

        await invalidateOperations();

        setOperationsGlobalActivityAndStatus({
          activity: activities.addOperation,
          status: "success",
        });
      },
      onError: () => {
        setOperationsGlobalActivityAndStatus({
          activity: activities.addOperation,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};
